const OFFER_PROPERTY_KEY = '_cst_lifecycle_offer';
const OFFER_PROPERTY_VALUE = 'freebag-v1';
const OFFER_LABEL_KEY = 'Offer';
const OFFER_LABEL_VALUE = 'Email offer item; eligibility checked at checkout';
const DEFAULT_TIMEOUT_MS = 8000;
const UNKNOWN_ADD_STORAGE_PREFIX = 'cst-lifecycle-gift-offer:unknown-add:';
const UNKNOWN_ACTION_STORAGE_PREFIX = 'cst-lifecycle-gift-offer:unknown-action:';
const unknownAddOutcomes = new Set();

class OfferRequestError extends Error {
  constructor(message, outcomeUnknown = false, status = null) {
    super(message);
    this.name = 'OfferRequestError';
    this.outcomeUnknown = outcomeUnknown;
    this.status = status;
  }
}

class CstLifecycleGiftOffer extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;
      this.statusElement = this.querySelector('[data-offer-status]');
      this.introElement = this.closest('.cst-lifecycle-gift__content')?.querySelector(
        '[data-offer-intro]'
      );
      this.contentElement = this.closest('.cst-lifecycle-gift__content');
      this.headingElement = this.contentElement?.querySelector('[data-offer-heading]');
      this.headingText = this.headingElement?.textContent.trim() || '';
      this.introMarkup = this.introElement?.innerHTML || '';
      this.qualifyingLink = this.querySelector('[data-qualifying-link]');
      this.retryButton = this.querySelector('[data-offer-retry]');
      this.manualRecoveryLink = this.querySelector('[data-offer-manual-recovery]');
      this.actionStatusElement = this.querySelector('[data-action-status]');
      this.actionButtons = [...this.querySelectorAll('[data-cart-add-button]')];
      this.receiptOptions = [...this.querySelectorAll('[data-receipt-option]')];
      this.receiptPanels = [...this.querySelectorAll('[data-receipt-panel]')];
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.cartUiGeneration = 0;
      this.cartIndicatorRequestId = 0;
      this.mode = this.dataset.mode || 'gift';
      this.variantId = this.dataset.variantId || '';
      this.unknownAddStorageKey = `${UNKNOWN_ADD_STORAGE_PREFIX}${this.variantId}`;
      this.timeoutMs = Number(this.dataset.timeoutMs) || DEFAULT_TIMEOUT_MS;
      if (this.dataset.pageTitle) document.title = this.dataset.pageTitle;
      this.handleRetry = () => {
        this.ensureOffer();
      };
      this.handlePageShow = (event) => {
        if (event.persisted) this.ensureOffer();
      };
      this.retryButton?.addEventListener('click', this.handleRetry);
      this.actionButtons.forEach((button) => {
        button.addEventListener('click', () => this.addConfiguredItem(button));
      });
      this.receiptOptions.forEach((button) => {
        button.addEventListener('click', () => this.selectReceipt(button.dataset.receiptOption));
      });
    }

    if (this.mode === 'code') {
      this.dataset.offerState = 'ready';
      if (this.contentElement) this.contentElement.dataset.offerVisualState = 'active';
      return;
    }

    if (this.dataset.customerState === 'ineligible') {
      this.setState('ineligible', this.dataset.messageIneligible);
      return;
    }

    if (!this.variantId || this.dataset.variantAvailable !== 'true') {
      this.setState('unavailable', this.dataset.messageUnavailable);
      return;
    }

    if (!this.pageShowListening) {
      window.addEventListener('pageshow', this.handlePageShow);
      this.pageShowListening = true;
    }
    this.ensureOffer();
  }

  disconnectedCallback() {
    if (this.pageShowListening) {
      window.removeEventListener('pageshow', this.handlePageShow);
      this.pageShowListening = false;
    }
  }

  ensureOffer() {
    if (this.pendingRequest) return this.pendingRequest;

    this.pendingRequest = this.processOffer().finally(() => {
      this.pendingRequest = null;
    });

    return this.pendingRequest;
  }

  async processOffer() {
    this.setState('checking', this.dataset.messageChecking, { busy: true });
    const hadUnknownAddOutcome = this.hasUnknownAddOutcome();

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (error) {
      if (hadUnknownAddOutcome || this.hasUnknownAddOutcome()) {
        this.showUnknownRecovery();
      } else {
        this.showError(this.dataset.messageCheckError);
      }
      return;
    }

    const existingLines = this.findOfferLines(cart);
    if (existingLines.length > 0) {
      await this.finishWithExistingOffer(existingLines, true);
      return;
    }

    if (hadUnknownAddOutcome || this.hasUnknownAddOutcome()) {
      this.showUnknownRecovery();
      return;
    }

    if (!this.markUnknownAddOutcome()) {
      this.clearUnknownAddOutcome();
      this.showAddError(
        new OfferRequestError(this.dataset.messageAddError, false, null)
      );
      return;
    }

    this.setState('adding', this.dataset.messageAdding, { busy: true });

    try {
      await this.addOffer();
    } catch (error) {
      if (error instanceof OfferRequestError && error.outcomeUnknown) {
        this.markUnknownAddOutcome();
        const reconciled = await this.reconcileUnknownAdd();
        if (reconciled) return;

        this.showUnknownRecovery();
        return;
      }

      this.clearUnknownAddOutcome();
      this.showAddError(error);
      return;
    }

    try {
      cart = await this.fetchCart();
    } catch (error) {
      this.markUnknownAddOutcome();
      this.showUnknownRecovery();
      return;
    }

    const confirmedLines = this.findOfferLines(cart);
    if (confirmedLines.length === 0) {
      this.markUnknownAddOutcome();
      this.showUnknownRecovery();
      return;
    }

    await this.finishWithExistingOffer(confirmedLines, false);
  }

  async reconcileUnknownAdd() {
    try {
      const cart = await this.fetchCart();
      const offerLines = this.findOfferLines(cart);
      if (offerLines.length === 0) return false;

      await this.finishWithExistingOffer(offerLines, false);
      return true;
    } catch (error) {
      return false;
    }
  }

  async finishWithExistingOffer(offerLines, wasAlreadyPresent) {
    const normalized = await this.normalizeOfferLines(offerLines);
    if (!normalized) {
      this.showError(this.dataset.messageQuantityError);
      return;
    }

    this.clearUnknownAddOutcome();
    this.setState(
      'success',
      wasAlreadyPresent ? this.dataset.messageAlreadyAdded : this.dataset.messageSuccess
    );
    this.refreshCartIndicator();
  }

  async refreshCartIndicator() {
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (!currentBubble || !this.dataset.cartPageUrl) return;
    const generation = this.cartUiGeneration;
    const requestId = ++this.cartIndicatorRequestId;

    try {
      const separator = this.dataset.cartPageUrl.includes('?') ? '&' : '?';
      const response = await fetch(
        `${this.dataset.cartPageUrl}${separator}section_id=cart-icon-bubble`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        }
      );
      if (!response.ok) return;

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement =
        parsed.getElementById('cart-icon-bubble') ||
        parsed.getElementById('shopify-section-cart-icon-bubble');
      const superseded =
        generation !== this.cartUiGeneration ||
        requestId !== this.cartIndicatorRequestId;
      if (replacement && !superseded) currentBubble.innerHTML = replacement.innerHTML;
    } catch (error) {
      // The cart mutation is authoritative; a stale badge should not downgrade it.
    }
  }

  findOfferLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];

    return cart.items.filter((item) => {
      const itemVariantId = String(item.variant_id || item.id || '');
      const properties = item.properties || {};

      return (
        itemVariantId === this.variantId &&
        properties[OFFER_PROPERTY_KEY] === OFFER_PROPERTY_VALUE
      );
    });
  }

  async normalizeOfferLines(offerLines) {
    const needsChange = offerLines.some((line, index) => {
      const desiredQuantity = index === 0 ? 1 : 0;
      return Number(line.quantity) !== desiredQuantity;
    });

    if (!needsChange) return true;

    try {
      for (const [index, line] of offerLines.entries()) {
        const desiredQuantity = index === 0 ? 1 : 0;
        if (Number(line.quantity) === desiredQuantity) continue;

        await this.changeLine(line.key, desiredQuantity);
      }
    } catch (error) {
      // A timed-out change might still have succeeded. Read before deciding.
    }

    try {
      const cart = await this.fetchCart();
      const reconciledLines = this.findOfferLines(cart);
      const totalQuantity = reconciledLines.reduce(
        (total, line) => total + Number(line.quantity || 0),
        0
      );

      return totalQuantity === 1;
    } catch (error) {
      return false;
    }
  }

  hasUnknownAddOutcome() {
    if (unknownAddOutcomes.has(this.unknownAddStorageKey)) return true;

    try {
      return window.localStorage.getItem(this.unknownAddStorageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  markUnknownAddOutcome() {
    unknownAddOutcomes.add(this.unknownAddStorageKey);

    try {
      window.localStorage.setItem(this.unknownAddStorageKey, 'true');
      return window.localStorage.getItem(this.unknownAddStorageKey) === 'true';
    } catch (error) {
      // Without a durable marker, a fresh document could repeat the add.
      return false;
    }
  }

  clearUnknownAddOutcome() {
    unknownAddOutcomes.delete(this.unknownAddStorageKey);

    try {
      window.localStorage.removeItem(this.unknownAddStorageKey);
    } catch (error) {
      // No action needed when storage is unavailable.
    }
  }

  fetchCart() {
    return this.fetchJson(this.dataset.cartUrl, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
  }

  addOffer() {
    return this.fetchJson(
      this.dataset.cartAddUrl,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(this.variantId),
              quantity: 1,
              properties: {
                [OFFER_PROPERTY_KEY]: OFFER_PROPERTY_VALUE,
                [OFFER_LABEL_KEY]: OFFER_LABEL_VALUE,
              },
            },
          ],
        }),
      },
      true
    );
  }

  addConfiguredItem(button) {
    if (
      !button ||
      button.disabled ||
      button.getAttribute('aria-disabled') === 'true' ||
      this.pendingActionRequest
    ) {
      return;
    }

    const variantId = String(button.dataset.addVariantId || '');
    if (!variantId) return;

    this.actionButtons.forEach((actionButton) => this.setActionBusy(actionButton, true));
    this.showActionStatus(this.dataset.messageItemAdding);
    this.pendingActionRequest = this.processConfiguredItem(button, variantId).finally(() => {
      this.actionButtons.forEach((actionButton) => this.setActionBusy(actionButton, false));
      this.pendingActionRequest = null;
    });

    return this.pendingActionRequest;
  }

  async processConfiguredItem(button, variantId) {
    const storageKey = `${UNKNOWN_ACTION_STORAGE_PREFIX}${variantId}`;
    const existingUncertainty = this.readActionUncertainty(storageKey);

    if (existingUncertainty) {
      try {
        const cart = await this.fetchCart();
        const quantity = this.countPaidVariant(cart, variantId);
        if (quantity > existingUncertainty.baseline) {
          this.cartUiGeneration += 1;
          this.clearActionUncertainty(storageKey);
          this.showActionStatus(this.dataset.messageItemAdded);
          this.refreshCartIndicator();
          return;
        }
      } catch (error) {
        // The previous write still cannot be reconciled. Stay GET-only.
      }

      this.showActionStatus(
        this.dataset.messageItemUnknown || this.dataset.messageItemAddError,
        true
      );
      return;
    }

    let cart;
    try {
      cart = await this.fetchCart();
    } catch (error) {
      this.showActionStatus(this.dataset.messageItemAddError, true);
      return;
    }

    const baseline = this.countPaidVariant(cart, variantId);
    if (!this.markActionUncertainty(storageKey, baseline)) {
      this.clearActionUncertainty(storageKey);
      this.showActionStatus(this.dataset.messageItemAddError, true);
      return;
    }

    this.cart?.setActiveElement?.(button);
    this.cartUiGeneration += 1;

    let response;
    try {
      response = await this.addCartItem(variantId);
    } catch (error) {
      if (!(error instanceof OfferRequestError && error.outcomeUnknown)) {
        this.clearActionUncertainty(storageKey);
        this.refreshCartIndicator();
      }
      const inventoryRejected = error instanceof OfferRequestError && error.status === 422;
      const outcomeUnknown = error instanceof OfferRequestError && error.outcomeUnknown;
      const message = inventoryRejected
        ? button.dataset.addOutOfStock ||
          this.dataset.messageItemOutOfStock ||
          this.dataset.messageItemAddError
        : outcomeUnknown
          ? this.dataset.messageItemUnknown || this.dataset.messageItemAddError
          : this.dataset.messageItemAddError;
      this.showActionStatus(message, true);
      return;
    }

    this.clearActionUncertainty(storageKey);
    this.showActionStatus(this.dataset.messageItemAdded);

    if (this.cart && response?.sections) {
      try {
        this.cart.renderContents(response);
      } catch (error) {
        // The cart mutation succeeded; the live status remains the fallback confirmation.
      }
    }
  }

  addCartItem(variantId) {
    const payload = {
      id: Number(variantId),
      quantity: 1,
    };

    if (this.cart?.getSectionsToRender) {
      payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }

    return this.fetchJson(
      this.dataset.cartAddUrl,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      },
      true
    );
  }

  countPaidVariant(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.reduce((total, item) => {
      const itemVariantId = String(item.variant_id || item.id || '');
      const properties = item.properties || {};
      const isOfferLine =
        properties[OFFER_PROPERTY_KEY] === OFFER_PROPERTY_VALUE;

      if (itemVariantId !== variantId || isOfferLine) return total;
      return total + Number(item.quantity || 0);
    }, 0);
  }

  readActionUncertainty(storageKey) {
    try {
      const value = window.localStorage.getItem(storageKey);
      if (!value) return null;

      const parsed = JSON.parse(value);
      if (!Number.isFinite(parsed.baseline)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  }

  markActionUncertainty(storageKey, baseline) {
    try {
      const value = JSON.stringify({ baseline });
      window.localStorage.setItem(storageKey, value);
      return window.localStorage.getItem(storageKey) === value;
    } catch (error) {
      return false;
    }
  }

  clearActionUncertainty(storageKey) {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // No action needed when storage is unavailable.
    }
  }

  setActionBusy(button, busy) {
    button.setAttribute('aria-disabled', busy ? 'true' : 'false');
    button.classList.toggle('loading', busy);
    const spinner = button.querySelector('[data-action-spinner]');
    if (spinner) spinner.hidden = !busy;
  }

  showActionStatus(message, isError = false) {
    if (!this.actionStatusElement || !message) return;

    this.actionStatusElement.hidden = false;
    this.actionStatusElement.textContent = message;
    this.actionStatusElement.dataset.status = isError ? 'error' : 'success';
  }

  selectReceipt(selectedOption) {
    if (!selectedOption) return;

    this.receiptOptions.forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        button.dataset.receiptOption === selectedOption ? 'true' : 'false'
      );
    });

    this.receiptPanels.forEach((panel) => {
      panel.hidden = panel.dataset.receiptPanel !== selectedOption;
    });
  }

  changeLine(lineKey, quantity) {
    return this.fetchJson(
      this.dataset.cartChangeUrl,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
        }),
      },
      true
    );
  }

  async fetchJson(url, options, mutation = false) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        signal: controller.signal,
      });

      let payload;
      try {
        payload = await response.json();
      } catch (error) {
        throw new OfferRequestError(
          this.dataset.messageAddError || 'Shopify returned an unreadable response.',
          mutation && response.ok,
          response.status
        );
      }

      if (!response.ok) {
        throw new OfferRequestError(
          payload.description || payload.message || this.dataset.messageAddError,
          false,
          response.status
        );
      }

      return payload;
    } catch (error) {
      if (error instanceof OfferRequestError) throw error;

      throw new OfferRequestError(
        this.dataset.messageUnknown || 'The cart request could not be confirmed.',
        mutation,
        null
      );
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  showError(message) {
    this.setState('error', message, { retry: true });
  }

  showAddError(error) {
    const inventoryRejected = error instanceof OfferRequestError && error.status === 422;
    const message = inventoryRejected
      ? this.dataset.messageOutOfStock || this.dataset.messageUnavailable
      : this.dataset.messageAddError || error?.message;

    this.setState(inventoryRejected ? 'unavailable' : 'error', message, {
      retry: true,
      manualRecovery: !inventoryRejected,
    });
  }

  showUnknownRecovery() {
    this.setState('unknown', this.dataset.messageUnknown, {
      retry: true,
    });
  }

  setState(state, message, options = {}) {
    this.dataset.offerState = state;
    this.toggleAttribute('aria-busy', options.busy === true);
    const terminalState = ['ineligible', 'unavailable', 'error', 'unknown'].includes(state);

    if (this.contentElement) {
      this.contentElement.dataset.offerVisualState = terminalState ? state : 'active';
    }

    if (this.headingElement) {
      this.headingElement.textContent =
        terminalState && message ? message : this.headingText;
    }

    if (this.statusElement && message) {
      this.statusElement.textContent = message;
    }

    if (this.introElement) {
      this.introElement.hidden = terminalState;

      if (!terminalState && state === 'success' && this.dataset.messageAdded) {
        const paragraph = document.createElement('p');
        paragraph.textContent = this.dataset.messageAdded;
        this.introElement.replaceChildren(paragraph);
      } else if (!terminalState) {
        this.introElement.innerHTML = this.introMarkup;
      }
    }

    if (this.retryButton) {
      this.retryButton.hidden = options.retry !== true;
    }

    if (this.manualRecoveryLink) {
      this.manualRecoveryLink.hidden = options.manualRecovery !== true;
    }
  }

}

if (!customElements.get('cst-lifecycle-gift-offer')) {
  customElements.define('cst-lifecycle-gift-offer', CstLifecycleGiftOffer);
}
